import { useFrame } from '@react-three/fiber'
import { Bloom, EffectComposer, Vignette } from '@react-three/postprocessing'
import { useMemo, useRef } from 'react'
import * as THREE from 'three'
import type { GameRuntime } from '../game/runtime'
import { WEAPON_MOUNT } from '../game/weapon-mount'
import {
  appendCappedParticles,
  type CombatParticleSeed,
  createCombatParticles,
  reconcileCombatEffectParticles,
} from './combat-effects'

const PROJECTILE_CAP = 64
const PARTICLE_CAP = 96

function ProjectilePool({ runtime }: { runtime: GameRuntime }) {
  const coreRef = useRef<THREE.InstancedMesh>(null)
  const sheathRef = useRef<THREE.InstancedMesh>(null)
  const dummy = useMemo(() => new THREE.Object3D(), [])
  useFrame(() => {
    const core = coreRef.current
    const sheath = sheathRef.current
    if (!core || !sheath) return
    const projectiles = runtime.getState().projectiles.slice(0, PROJECTILE_CAP)
    projectiles.forEach((projectile, index) => {
      dummy.position.set(projectile.position.x, WEAPON_MOUNT.muzzle[1], projectile.position.y)
      dummy.rotation.y = Math.atan2(projectile.velocity.x, projectile.velocity.y)
      dummy.scale.set(1, 1, 1)
      dummy.updateMatrix()
      core.setMatrixAt(index, dummy.matrix)
      dummy.scale.set(2.8, 2.8, 1.16)
      dummy.updateMatrix()
      sheath.setMatrixAt(index, dummy.matrix)
    })
    for (let index = projectiles.length; index < PROJECTILE_CAP; index += 1) {
      dummy.scale.setScalar(0)
      dummy.updateMatrix()
      core.setMatrixAt(index, dummy.matrix)
      sheath.setMatrixAt(index, dummy.matrix)
    }
    core.instanceMatrix.needsUpdate = true
    sheath.instanceMatrix.needsUpdate = true
  })
  return (
    <>
      <instancedMesh ref={sheathRef} args={[undefined, undefined, PROJECTILE_CAP]} frustumCulled={false}>
        <boxGeometry args={[0.07, 0.07, 1.7]} />
        <meshBasicMaterial
          color="#ff763b"
          transparent
          opacity={0.44}
          depthWrite={false}
          toneMapped={false}
          blending={THREE.AdditiveBlending}
        />
      </instancedMesh>
      <instancedMesh ref={coreRef} args={[undefined, undefined, PROJECTILE_CAP]} frustumCulled={false}>
        <boxGeometry args={[0.055, 0.055, 1.45]} />
        <meshBasicMaterial color="#fff7d6" depthWrite={false} toneMapped={false} />
      </instancedMesh>
    </>
  )
}

interface ActiveParticle extends CombatParticleSeed {
  age: number
  initialTtl: number
}

function CombatParticlePool({ runtime }: { runtime: GameRuntime }) {
  const smokeRef = useRef<THREE.InstancedMesh>(null)
  const casingRef = useRef<THREE.InstancedMesh>(null)
  const sparkRef = useRef<THREE.InstancedMesh>(null)
  const ringRef = useRef<THREE.InstancedMesh>(null)
  const particlesRef = useRef<ActiveParticle[]>([])
  const lastEventIdRef = useRef(0)
  const contactCooldownRef = useRef(new Map<string, number>())
  const dummy = useMemo(() => new THREE.Object3D(), [])
  const lookAt = useMemo(() => new THREE.Vector3(), [])

  useFrame((_, delta) => {
    const smoke = smokeRef.current
    const casing = casingRef.current
    const spark = sparkRef.current
    const ring = ringRef.current
    if (!smoke || !casing || !spark || !ring) return

    const state = runtime.getState()
    const reconciledParticles = reconcileCombatEffectParticles(
      state.nextEventId,
      lastEventIdRef.current,
      particlesRef.current,
    )
    if (reconciledParticles !== particlesRef.current) {
      lastEventIdRef.current = 0
      particlesRef.current = reconciledParticles
      contactCooldownRef.current.clear()
    }
    const incoming = state.events
      .filter((event) => event.id > lastEventIdRef.current)
      .filter((event) => {
        if (event.type !== 'vehicle-contact' || !event.targetId) return true
        const pair = [event.vehicleId, event.targetId].sort().join(':')
        const lastContact = contactCooldownRef.current.get(pair) ?? Number.NEGATIVE_INFINITY
        if (state.time - lastContact < 0.18) return false
        contactCooldownRef.current.set(pair, state.time)
        return true
      })
      .flatMap((event) => createCombatParticles(event, state))
      .map((particle): ActiveParticle => ({ ...particle, age: 0, initialTtl: particle.ttl }))
    if (state.events.length > 0) {
      lastEventIdRef.current = Math.max(lastEventIdRef.current, ...state.events.map((event) => event.id))
    }
    particlesRef.current = appendCappedParticles(particlesRef.current, incoming, PARTICLE_CAP)

    const safeDelta = Math.min(delta, 0.05)
    for (const particle of particlesRef.current) {
      particle.age += safeDelta
      particle.ttl -= safeDelta
      particle.position.x += particle.velocity.x * safeDelta
      particle.position.y += particle.velocity.y * safeDelta
      particle.position.z += particle.velocity.z * safeDelta
      if (particle.kind === 'smoke') {
        const drag = Math.exp(-safeDelta * 2.7)
        particle.velocity.x *= drag
        particle.velocity.z *= drag
        particle.velocity.y += safeDelta * 0.5
      } else if (particle.kind !== 'ring') {
        particle.velocity.y -= safeDelta * (particle.kind === 'casing' ? 12 : 18)
      }
      if (particle.kind === 'casing' && particle.position.y < 0.12) {
        particle.position.y = 0.12
        particle.velocity.y = Math.abs(particle.velocity.y) * 0.26
        particle.velocity.x *= 0.72
        particle.velocity.z *= 0.72
      }
    }
    particlesRef.current = particlesRef.current.filter((particle) => particle.ttl > 0)

    let smokeIndex = 0
    let casingIndex = 0
    let sparkIndex = 0
    let ringIndex = 0
    for (const particle of particlesRef.current) {
      const life = Math.max(0, particle.ttl / particle.initialTtl)
      dummy.position.set(particle.position.x, particle.position.y, particle.position.z)
      dummy.rotation.set(0, 0, 0)
      if (particle.kind === 'smoke' && smokeIndex < PARTICLE_CAP) {
        const size = 0.22 + (1 - life) * 0.58
        dummy.rotation.set(particle.spin, particle.spin * 0.7, 0)
        dummy.scale.setScalar(size * Math.min(1, particle.age * 12) * life)
        dummy.updateMatrix()
        smoke.setMatrixAt(smokeIndex, dummy.matrix)
        smokeIndex += 1
      } else if (particle.kind === 'casing' && casingIndex < PARTICLE_CAP) {
        dummy.rotation.set(particle.age * particle.spin, particle.spin, particle.age * particle.spin * 0.7)
        dummy.scale.set(1, 1, 1)
        dummy.updateMatrix()
        casing.setMatrixAt(casingIndex, dummy.matrix)
        casingIndex += 1
      } else if (particle.kind === 'spark' && sparkIndex < PARTICLE_CAP) {
        lookAt.set(
          particle.position.x + particle.velocity.x,
          particle.position.y + particle.velocity.y,
          particle.position.z + particle.velocity.z,
        )
        dummy.lookAt(lookAt)
        dummy.scale.set(life, life, 0.45 + life * 1.4)
        dummy.updateMatrix()
        spark.setMatrixAt(sparkIndex, dummy.matrix)
        sparkIndex += 1
      } else if (particle.kind === 'ring' && ringIndex < PARTICLE_CAP) {
        dummy.rotation.set(Math.PI / 2, 0, particle.spin)
        dummy.scale.setScalar((0.5 + particle.age * 8) * life)
        dummy.updateMatrix()
        ring.setMatrixAt(ringIndex, dummy.matrix)
        ringIndex += 1
      }
    }

    for (const [mesh, used] of [
      [smoke, smokeIndex],
      [casing, casingIndex],
      [spark, sparkIndex],
      [ring, ringIndex],
    ] as const) {
      for (let index = used; index < PARTICLE_CAP; index += 1) {
        dummy.scale.setScalar(0)
        dummy.updateMatrix()
        mesh.setMatrixAt(index, dummy.matrix)
      }
      mesh.instanceMatrix.needsUpdate = true
    }
  })

  return (
    <>
      <instancedMesh ref={smokeRef} args={[undefined, undefined, PARTICLE_CAP]} frustumCulled={false}>
        <dodecahedronGeometry args={[1, 0]} />
        <meshStandardMaterial color="#6c707b" roughness={1} transparent opacity={0.34} depthWrite={false} />
      </instancedMesh>
      <instancedMesh ref={casingRef} args={[undefined, undefined, PARTICLE_CAP]} frustumCulled={false}>
        <cylinderGeometry args={[0.035, 0.045, 0.16, 8]} />
        <meshStandardMaterial color="#d6a640" metalness={0.92} roughness={0.28} />
      </instancedMesh>
      <instancedMesh ref={sparkRef} args={[undefined, undefined, PARTICLE_CAP]} frustumCulled={false}>
        <boxGeometry args={[0.045, 0.045, 0.5]} />
        <meshBasicMaterial
          color="#ffb137"
          transparent
          opacity={0.92}
          depthWrite={false}
          toneMapped={false}
          blending={THREE.AdditiveBlending}
        />
      </instancedMesh>
      <instancedMesh ref={ringRef} args={[undefined, undefined, PARTICLE_CAP]} frustumCulled={false}>
        <torusGeometry args={[0.72, 0.045, 6, 28]} />
        <meshBasicMaterial
          color="#ff426b"
          transparent
          opacity={0.78}
          depthWrite={false}
          toneMapped={false}
          blending={THREE.AdditiveBlending}
        />
      </instancedMesh>
    </>
  )
}

function Rain({ runtime }: { runtime: GameRuntime }) {
  const ref = useRef<THREE.Points>(null)
  const positions = useMemo(() => {
    const values = new Float32Array(1500 * 3)
    for (let index = 0; index < 1500; index += 1) {
      values[index * 3] = Math.sin(index * 12.9898) * 55
      values[index * 3 + 1] = ((index * 7.13) % 34) + 2
      values[index * 3 + 2] = Math.cos(index * 78.233) * 55
    }
    return values
  }, [])
  useFrame((_, delta) => {
    const points = ref.current
    const player = runtime.getState().vehicles[0]
    if (!points || !player) return
    points.position.set(player.position.x, 0, player.position.y)
    const attribute = points.geometry.getAttribute('position') as THREE.BufferAttribute
    for (let index = 0; index < attribute.count; index += 1) {
      const y = attribute.getY(index) - delta * 28
      attribute.setY(index, y < 0 ? y + 36 : y)
    }
    attribute.needsUpdate = true
  })
  return (
    <points ref={ref} frustumCulled={false}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial color="#9ddcff" size={0.055} transparent opacity={0.38} depthWrite={false} />
    </points>
  )
}

export function RaceEffects({ runtime, reducedMotion }: { runtime: GameRuntime; reducedMotion: boolean }) {
  return (
    <>
      <ProjectilePool runtime={runtime} />
      <CombatParticlePool runtime={runtime} />
      {!reducedMotion && <Rain runtime={runtime} />}
      <EffectComposer multisampling={0} enableNormalPass={false}>
        <Bloom luminanceThreshold={0.42} luminanceSmoothing={0.18} mipmapBlur intensity={1.35} />
        <Vignette eskil={false} offset={0.18} darkness={0.72} />
      </EffectComposer>
    </>
  )
}
